import axios, { AxiosError } from "axios";
import { Option } from "../common/api/Model";
import { ApiResult, ErrorResponseData, getAdminHeaders } from "../common/api/Api";
import { DEFAULT_TIMEOUT, getAppEnv } from "../common/settings";
import { TenantForm } from "./useRegisterTenantButton";
import { getAuthItems } from "../auth/AuthItems";

const appEnv = getAppEnv();

export interface AdminSerial {
  [index: string]: string | number | number[];
  id: number;
  tenantId: string;
  serialNumber: string;
  familyName: string;
  givenName: string;
  loginMailAddress: string;
  useCategory: string;
  country: string;
  tenantName: string;
  optionIdList: number[];
}

export interface OutputAdminSerial {
  [index: string]: string;
  tenantId: string;
  tenantName: string;
  name: string;
  loginMailAddress: string;
  useCategory: string;
  country: string;
  serialNumber: string;
}

export interface AdminData {
  options: Option[];
  list: AdminSerial[];
}

export interface TenantData {
  id: number;
  tenantId: string;
  customerName: string;
  contractors: Contractor[];
}

export interface Contractor {
  familyName: string;
  givenName: string;
  mailAddress: string;
  state: string;
}

export interface TenantsList {
  results: TenantData[];
}

type SerialDataResult = ApiResult<AdminData, ErrorResponseData>;

export const getListForAdmin = async (): Promise<SerialDataResult> => {
  try {
    const url = `${appEnv.webApiEndPoint}/app/api/admin/list/${getAuthItems().loginPole}`;
    const headers = { headers: getAdminHeaders(), timeout: DEFAULT_TIMEOUT };
    const response = await axios.get<AdminData>(url, headers);

    const data = response.data;
    return { data: data };
  } catch (e) {
    const err = e as AxiosError<ErrorResponseData>;
    return { error: err };
  }
};

export const deleteSerial = async (id: number): Promise<ApiResult<string, ErrorResponseData>> => {
  try {
    const url = `${appEnv.webApiEndPoint}/app/api/admin/deleteSerial`;
    const requestBody = { id: id };
    const headers = { headers: getAdminHeaders(), timeout: DEFAULT_TIMEOUT };
    const response = await axios.post<string>(url, requestBody, headers);
    return { data: response.data };
  } catch (e) {
    const err = e as AxiosError<ErrorResponseData>;
    return { error: err };
  }
};

type TenantDataResult = ApiResult<TenantsList, ErrorResponseData>;

export const getTenantList = async (): Promise<TenantDataResult> => {
  try {
    const url = `${appEnv.webApiEndPoint}/app/api/admin/tenantList`;
    const headers = { headers: getAdminHeaders(), timeout: DEFAULT_TIMEOUT };
    const response = await axios.get<TenantsList>(url, headers);

    const data = response.data;
    return { data: data };
  } catch (e) {
    const err = e as AxiosError<ErrorResponseData>;
    return { error: err };
  }
};

export const searchTenantsForMailAddress = async (contractorMailAddress: string): Promise<TenantDataResult> => {
  try {
    const url = `${appEnv.webApiEndPoint}/app/api/admin/searchTenants`;
    const requestBody = { mailAddress: contractorMailAddress };
    const headers = { headers: getAdminHeaders(), timeout: DEFAULT_TIMEOUT + 10000 }; // デフォルトよりも時間が掛かる
    const response = await axios.post<TenantsList>(url, requestBody, headers);

    const data = response.data;
    return { data: data };
  } catch (e) {
    const err = e as AxiosError<ErrorResponseData>;
    return { error: err };
  }
};

type AddTenantDataResult = ApiResult<TenantData, ErrorResponseData>;

export const addTenant = async (addRsiCustomerTenantRequest: TenantForm): Promise<AddTenantDataResult> => {
  try {
    const url = `${appEnv.webApiEndPoint}/app/api/admin/addTenant`;
    const requestBody = {
      companyName: addRsiCustomerTenantRequest.companyName,
      useCategory: addRsiCustomerTenantRequest.useCategory,
      country: addRsiCustomerTenantRequest.country,
      state: addRsiCustomerTenantRequest.state,
      givenName: addRsiCustomerTenantRequest.givenName,
      familyName: addRsiCustomerTenantRequest.familyName,
      emailAddress: addRsiCustomerTenantRequest.emailAddress,
      contractType: addRsiCustomerTenantRequest.contractType,
      date: new Date().toISOString(),
    };
    const headers = { headers: getAdminHeaders(), timeout: DEFAULT_TIMEOUT };
    const response = await axios.post<TenantData>(url, requestBody, headers);

    const data = response.data;
    return { data: data };
  } catch (e) {
    const err = e as AxiosError<ErrorResponseData>;
    return { error: err };
  }
};

type UpdateTenantDataResult = ApiResult<TenantData, ErrorResponseData>;

export const updateTenant = async (
  customerTenantId: string,
  contractorId: string,
  updateRsiCustomerTenantRequest: TenantForm,
): Promise<UpdateTenantDataResult> => {
  try {
    const url = `${appEnv.webApiEndPoint}/app/api/admin/updateTenant`;
    const requestBody = {
      customerTenantId: customerTenantId,
      contractorId: contractorId,
      companyName: updateRsiCustomerTenantRequest.companyName,
      useCategory: updateRsiCustomerTenantRequest.useCategory,
      country: updateRsiCustomerTenantRequest.country,
      givenName: updateRsiCustomerTenantRequest.givenName,
      familyName: updateRsiCustomerTenantRequest.familyName,
      emailAddress: updateRsiCustomerTenantRequest.emailAddress,
      contractType: updateRsiCustomerTenantRequest.contractType,
    };
    const headers = { headers: getAdminHeaders(), timeout: DEFAULT_TIMEOUT };
    const response = await axios.post<TenantData>(url, requestBody, headers);

    const data = response.data;
    return { data: data };
  } catch (e) {
    const err = e as AxiosError<ErrorResponseData>;
    return { error: err };
  }
};
